import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { scrollToSection } from './lib/sectionNav';

import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FeaturedWorkshop } from './components/FeaturedWorkshop';
import { CurriculumScroll } from './components/CurriculumScroll';
import { EnrollSection } from './components/EnrollSection';
import { FaqSection } from './components/FaqSection';
import { RegistrationModal } from './components/RegistrationModal';

import { LEGAL_ROUTES } from './pages/legalRoutes';

import { Loader2, AlertTriangle } from 'lucide-react';

/**
 * Everything below is loaded on demand.
 *
 * The landing page is the thing paid traffic waits for, so it ships alone. The
 * checkout gateway, the invoice (which pulls in a PDF renderer), the policy
 * pages and the whole admin console are fetched only when they are opened.
 */
const CheckoutModal = lazy(() =>
  import('./components/CheckoutModal').then((m) => ({ default: m.CheckoutModal }))
);
const ReceiptModal = lazy(() =>
  import('./components/ReceiptModal').then((m) => ({ default: m.ReceiptModal }))
);
const AdminPanel = lazy(() =>
  import('./components/admin/AdminPanel').then((m) => ({ default: m.AdminPanel }))
);
const LegalPage = lazy(() =>
  import('./pages/LegalPage').then((m) => ({ default: m.LegalPage }))
);

/** Full-screen spinner shown while a lazy chunk downloads. */
function ChunkFallback() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 backdrop-blur-sm">
      <Loader2 className="w-7 h-7 text-white animate-spin" />
    </div>
  );
}

/**
 * Blocking overlay shown while we verify a payment the customer has just
 * returned from. Without it they land on the page with no idea whether their
 * money went through.
 */
function PaymentReturnOverlay() {
  const { returnState, closeModal, adminSettings } = useApp();
  if (!returnState) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-950/75 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-xl max-w-sm w-full p-8 text-center animate-pop-in">
        {returnState === 'checking' ? (
          <>
            <span className="inline-flex w-14 h-14 rounded-full bg-pay-500/10 items-center justify-center">
              <Loader2 className="w-6 h-6 text-pay-500 animate-spin" />
            </span>
            <h2 className="mt-4 font-display text-lg font-bold text-ink-900">
              Confirming your payment
            </h2>
            <p className="mt-2 text-sm text-ink-500 leading-relaxed">
              Checking with the payment gateway — this takes a few seconds. Please don't close
              this page.
            </p>
          </>
        ) : (
          <>
            <span className="inline-flex w-14 h-14 rounded-full bg-brand-50 items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-brand-600" />
            </span>
            <h2 className="mt-4 font-display text-lg font-bold text-ink-900">
              We couldn't confirm that payment
            </h2>
            <p className="mt-2 text-sm text-ink-500 leading-relaxed">
              If money left your account, email{' '}
              <a
                href={`mailto:${adminSettings.supportEmail}`}
                className="text-brand-700 font-semibold"
              >
                {adminSettings.supportEmail}
              </a>{' '}
              with your order reference and we will sort it out the same day.
            </p>
            <button onClick={closeModal} className="btn btn-outline btn-md w-full mt-6">
              Back to the workshop
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The first-visit shape of the page, held while the catalogue is still
 * arriving.
 *
 * A visitor with a warm cache never sees this — the workshop renders straight
 * from localStorage. It exists for the very first hit, where showing "no
 * workshop is open" for a second before the real one appears reads as a broken
 * page on the traffic we are paying for.
 */
function WorkshopSkeleton() {
  return (
    <div className="bg-ink-950" aria-hidden="true">
      <div className="w-full aspect-[4/3] skeleton" />
      <div className="shell -mt-16 pb-10 space-y-3">
        <div className="h-3 w-28 rounded-full skeleton" />
        <div className="h-8 w-full rounded-xl skeleton" />
        <div className="h-8 w-3/4 rounded-xl skeleton" />
        <div className="h-3 w-full rounded-full skeleton mt-5" />
        <div className="h-3 w-5/6 rounded-full skeleton" />
        <div className="grid grid-cols-3 gap-2.5 pt-4">
          <div className="h-20 rounded-2xl skeleton" />
          <div className="h-20 rounded-2xl skeleton" />
          <div className="h-20 rounded-2xl skeleton" />
        </div>
        <div className="h-14 w-full rounded-2xl skeleton mt-5" />
      </div>
    </div>
  );
}

/** Shown only if the catalogue is empty — a page with no workshop to sell. */
function NoWorkshop() {
  const { adminSettings } = useApp();
  return (
    <div className="shell py-24 text-center">
      <h1 className="font-display text-display-md font-bold text-ink-900">
        No workshop is open right now
      </h1>
      <p className="mt-3 text-sm text-ink-500 leading-relaxed">
        The next batch is being scheduled. Email{' '}
        <a href={`mailto:${adminSettings.supportEmail}`} className="text-brand-700 font-semibold">
          {adminSettings.supportEmail}
        </a>{' '}
        and we will tell you the moment dates are up.
      </p>
    </div>
  );
}

/**
 * The whole public page: one workshop, its reviews, its curriculum, its
 * checkout. In that order, as one continuous scroll.
 */
function LandingPage() {
  const { workshops, adminSettings, firestoreStatus } = useApp();

  // The admin picks which workshop the page sells; without a pick, the first
  // one in the catalogue. Never more than one — this page is a single funnel.
  const workshop = useMemo(() => {
    if (workshops.length === 0) return null;
    return (
      workshops.find((item) => item.id === adminSettings.featuredWorkshopId) || workshops[0]
    );
  }, [workshops, adminSettings.featuredWorkshopId]);

  // "Nothing is running" and "we have not heard back yet" are different
  // answers, and only one of them should be shown as a dead end.
  if (!workshop) {
    return firestoreStatus === 'syncing' ? <WorkshopSkeleton /> : <NoWorkshop />;
  }

  return (
    <main>
      <FeaturedWorkshop workshop={workshop} />
      <CurriculumScroll workshop={workshop} />
      <EnrollSection workshop={workshop} />
      <FaqSection workshop={workshop} />
    </main>
  );
}

function Shell() {
  const { route, activeModal } = useApp();

  // A shared `/#curriculum` link lands here before the section has rendered, so
  // the browser's own anchor jump finds nothing. Do it once React has mounted.
  useEffect(() => {
    if (window.location.hash) scrollToSection(window.location.hash);
  }, []);

  const isLegalRoute = LEGAL_ROUTES.includes(route);

  // `.phone-app` is the single phone-width column the whole public site lives
  // in — centred and letterboxed on anything wider than a handset.
  return (
    <div className="phone-app flex flex-col">
      <Header />

      <div className="flex-1">
        <Suspense fallback={<div className="h-screen" />}>
          {isLegalRoute ? <LegalPage path={route} /> : <LandingPage />}
        </Suspense>
      </div>

      <Footer />

      {/* Checkout: details form → Cashfree → receipt. */}
      {activeModal === 'registration' && <RegistrationModal />}

      <Suspense fallback={<ChunkFallback />}>
        {activeModal === 'checkout' && <CheckoutModal />}
        {activeModal === 'receipt' && <ReceiptModal />}
        {activeModal === 'admin' && <AdminPanel />}
      </Suspense>

      <PaymentReturnOverlay />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
